import { useState, useEffect } from 'react';
import { CheckCircle2, Loader2, FileQuestion, BookOpen, Users, Building2 } from 'lucide-react';

interface CategoryBreakdown {
  qa?: number;
  case_studies?: number;
  team_members?: number;
  company_sections?: number;
}

interface AnalysisTickerProps {
  isActive: boolean;
  onComplete?: () => void;
  extractedCount?: number;
  categories?: CategoryBreakdown;
  filesProcessed?: number;
  duplicatesFound?: number;
}

const PROCESSING_DURATION = 8400; // roughly matches old ticker cycle

export function AnalysisTicker({ isActive, onComplete, extractedCount = 0, categories, filesProcessed, duplicatesFound }: AnalysisTickerProps) {
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (!isActive) { setIsComplete(false); return; }

    const timer = setTimeout(() => {
      setIsComplete(true);
      onComplete?.();
    }, PROCESSING_DURATION);

    return () => clearTimeout(timer);
  }, [isActive, onComplete]);

  if (!isActive && !isComplete) return null;

  const categoryItems = [];
  if (categories?.qa && categories.qa > 0) categoryItems.push({ icon: FileQuestion, label: 'Q&A Pairs', count: categories.qa });
  if (categories?.case_studies && categories.case_studies > 0) categoryItems.push({ icon: BookOpen, label: 'Case Studies', count: categories.case_studies });
  if (categories?.team_members && categories.team_members > 0) categoryItems.push({ icon: Users, label: 'Team Members', count: categories.team_members });
  if (categories?.company_sections && categories.company_sections > 0) categoryItems.push({ icon: Building2, label: 'Company Sections', count: categories.company_sections });

  return (
    <div style={{ padding: 14, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
      {!isComplete && (
        <div className="flex items-center gap-3">
          <Loader2 className="w-4 h-4 animate-spin shrink-0" style={{ color: '#C2541A' }} />
          <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>
            Extracting content from your documents...
          </span>
        </div>
      )}

      {isComplete && (
        <div className="space-y-2">
          <div className="flex items-center gap-2" style={{ color: '#22c55e', fontWeight: 500 }}>
            <CheckCircle2 className="w-4 h-4 shrink-0" />
            <span style={{ fontSize: 12 }}>
              ✓ {extractedCount} knowledge assets indexed
              {filesProcessed && filesProcessed > 1 ? ` from ${filesProcessed} files` : ''}
            </span>
          </div>
          {duplicatesFound && duplicatesFound > 0 && (
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', paddingLeft: 24 }}>
              {duplicatesFound} duplicate{duplicatesFound !== 1 ? 's' : ''} detected and merged
            </div>
          )}
          {categoryItems.length > 0 && (
            <div style={{ paddingLeft: 24, borderLeft: '2px solid rgba(34,197,94,0.2)', marginLeft: 8 }} className="space-y-1">
              {categoryItems.map((item, idx) => (
                <div key={idx} className="flex items-center gap-2" style={{ color: 'rgba(255,255,255,0.45)' }}>
                  <item.icon className="w-3.5 h-3.5" />
                  <span style={{ fontSize: 11 }}>{item.count} {item.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
